import { LegalPage, MailTo, OPERATOR, Section } from "./LegalPage";

/**
 * DMCA notice-and-takedown policy.
 *
 * This page states a real policy — notices are read and acted on — but it does NOT by
 * itself confer safe harbour. Protection under 17 U.S.C. §512(c) requires a Designated
 * Agent registered with the U.S. Copyright Office (dmca.copyright.gov); registration
 * carries a fee and a three-yearly renewal, and no website text substitutes for it.
 *
 * No agent is named here, deliberately, because none is registered. Naming one who is not
 * on the Copyright Office's register would claim a protection that does not exist, and
 * these pages are worth exactly as much as their accuracy. Notices go to the general
 * inbox and are handled; the safe harbour is a separate thing to go and get.
 */
export function Dmca() {
  return (
    <LegalPage title="DMCA / Copyright Policy" updated="August 4, 2026">
      <p>
        {OPERATOR} respects the intellectual property of others and responds to notices of
        alleged copyright infringement under the Digital Millennium Copyright Act
        (17 U.S.C. §512). If you believe material on goonlords infringes your copyright,
        send a notice as described below and we will act promptly.
      </p>

      <Section title="Where to send a notice">
        Send copyright notices and counter-notifications by email:
        <div className="mt-2 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] p-3">
          <MailTo subject="DMCA notice" />
        </div>
      </Section>

      <Section title="Notice of claimed infringement">
        To be effective, your written notice must include substantially all of the
        following (17 U.S.C. §512(c)(3)):
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>A physical or electronic signature of the copyright owner, or a person
            authorised to act on their behalf.</li>
          <li>Identification of the copyrighted work claimed to have been infringed, or a
            representative list if multiple works are covered by one notice.</li>
          <li>Identification of the material claimed to be infringing, with information
            reasonably sufficient to let us locate it — a direct URL is best.</li>
          <li>Your contact information: name, address, telephone number, and email.</li>
          <li>A statement that you have a good-faith belief that the use is not authorised
            by the copyright owner, its agent, or the law.</li>
          <li>A statement that the information in the notice is accurate, and — under
            penalty of perjury — that you are the owner or authorised to act on the
            owner&rsquo;s behalf.</li>
        </ol>
        <p className="mt-2">
          An incomplete notice may delay our response. Please send one notice per claim
          where practical.
        </p>
      </Section>

      <Section title="What we do on receipt">
        On receiving a compliant notice we will remove or disable access to the material
        expeditiously, and make a reasonable effort to notify the user who submitted it.
      </Section>

      <Section title="Counter-notification">
        If you believe your material was removed by mistake or misidentification, you may
        send a counter-notification to the address above containing:
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>Your physical or electronic signature.</li>
          <li>Identification of the material and the location where it appeared before
            removal.</li>
          <li>A statement under penalty of perjury that you have a good-faith belief the
            material was removed as a result of mistake or misidentification.</li>
          <li>Your name, address, and telephone number, and a statement that you consent to
            the jurisdiction of the federal district court for your district (or, if you are
            outside the United States, any district in which we may be found), and that you
            will accept service of process from the complaining party.</li>
        </ol>
        <p className="mt-2">
          We may restore the material in 10 to 14 business days unless the complaining party
          notifies us that it has filed an action seeking a court order.
        </p>
      </Section>

      <Section title="Repeat infringers">
        We maintain a policy of terminating, in appropriate circumstances, the accounts of
        users who are repeat infringers.
      </Section>

      <Section title="Misrepresentation">
        Under 17 U.S.C. §512(f), a person who knowingly materially misrepresents that
        material is infringing — or was removed by mistake — may be liable for damages,
        including costs and legal fees.
      </Section>

      <Section title="Non-copyright reports">
        This page is for copyright only. To report content depicting a minor, a
        non-consenting person, or any other urgent abuse, use the routes on our{" "}
        <a href="/contact" className="text-[var(--color-accent)] hover:underline">
          contact page
        </a>{" "}
        — those are handled ahead of everything else.
      </Section>
    </LegalPage>
  );
}
